// TODO: remove with https://github.com/combinaut/sparkle/issues/32203 once admin JS imports sp apps directly.
import { blankSelectItem, selectValue } from "#/admin/legacy/sp-select/index.js";
import { whenSelectsReady } from "#/admin/legacy/sp-select/when-selects-ready.js";

const anchorChangeListeners = new WeakMap();
const restoredLinkTargets = new WeakMap();

/** Sets `control` to the choice matching `value`, or to its first enabled choice; does nothing without a control. */
export function selectChoice(control, value) {
  if (!control) {
    return;
  }

  control.value = selectValue(control.items, value);
}

/** Returns the title of the item `control` selects, or "" when it selects none. */
export function selectedTitle(control) {
  return control.items.find((item) => item.value === control.value)?.title || "";
}

/**
 * Lists the page's anchors after a "No Anchor" blank and selects `requested`, else the current value, else the first
 * anchor.
 */
export function fillInpageAnchors(control, anchors, requested) {
  const blank = {
    ...blankSelectItem(),
    title: globalThis.Sparkle.t("admin.components.messages.no_anchor"),
    pendingChoice: false,
  };
  const items = anchors.map(anchorItem);

  control.items = [blank, ...items];
  control.value = selectValue(control.items, requested ?? (control.value || items[0]?.value));
}

/**
 * Lists the fetched page's anchors in `group`'s picker and selects `hash`, which a row of its own lists when the page
 * does not. Shows the picker only when the page has anchors; `onChange` receives each later pick, "" once cleared.
 */
export function showLinkAnchors(group, data, hash, onChange) {
  const control = group.querySelector("#link-anchor-select");
  const anchor = hash ? hash.replace(/^#/, "") : "";
  const listed = data.anchor_items.some((item) => item.value === anchor);

  control.items =
    listed || !anchor ? data.anchor_items : [...data.anchor_items, { title: `#${anchor}`, value: anchor, dataset: {} }];
  control.value = selectValue(control.items, anchor);
  group.classList.toggle("visually-hidden", !data.anchors?.length);
  control.removeEventListener("change", anchorChangeListeners.get(control));

  function handleAnchorChange() {
    onChange(control.value ?? "");
  }

  anchorChangeListeners.set(control, handleAnchorChange);
  control.addEventListener("change", handleAnchorChange);
}

/**
 * Restores `url` and, when metadata loads, its preview and anchors. Metadata fetch failures retain `url`.
 * After a successful restoration, a different target stays unavailable for completion until its metadata loads.
 * Without a successful restoration, or on the same target, fetch failures allow completion. Preview and anchor
 * failures reject.
 */
export async function restoreInternalLink(link, url) {
  let data;
  const target = url.replace(/#.*/, "");
  link.metadataReady = false;

  try {
    data = await link.fetchResourceMetadata(url);
  } catch {
    link.setURL(url);
    link.metadataReady = !restoredLinkTargets.has(link) || restoredLinkTargets.get(link) === target;

    return;
  }

  link.updatePreview(data);
  link.setURL(url);
  await link.updateAnchors(data);
  restoredLinkTargets.set(link, target);
  link.metadataReady = true;
}

/**
 * Resolves to true once the link dialog in `root` holds `value` and `setURL`'s restoration has settled, or undefined
 * if a select fails to mount.
 */
export function fillLinkDialog(root, value, { setTarget, setURL }) {
  async function fillControls() {
    if (!value.documentTitle) {
      root.querySelector(".link-dialog-title").value = value.title ?? "";
    }

    selectChoice(root.querySelector(".link-dialog-link-style-select"), value.style);
    selectChoice(root.querySelector(".link-dialog-link-behaviour-select"), value.behaviour);
    setTarget(value.target);
    await setURL(value.url);

    return true;
  }

  return whenSelectsReady(root.querySelectorAll("sp-select"), fillControls);
}

/** Completes and closes `dialog` once its selects are ready, unless its active internal target has `metadataReady: false`. Returns whether it completed. */
export function completeLinkDialog(dialog) {
  const root = dialog.el?.[0] ?? dialog.el;
  const internalSelected = root?.querySelector(".tab-selectors input:checked")?.value === "tab_link-internal";

  if (!dialog.selectsReady || (internalSelected && dialog.internalLink?.metadataReady === false)) {
    return false;
  }

  dialog.callback?.(dialog.getValue());
  dialog.modal.remove();

  return true;
}

function anchorItem(anchor) {
  return {
    title: anchor.label.length > 40 ? anchor.label.slice(0, 40) + "..." : anchor.label,
    value: anchor.elementId,
    dataset: {},
  };
}
